import { useMemo, useState } from 'react'
import { Keyboard, Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'

import { BASE_URL } from '@/constants/config'
import { cn } from '@/utils/cn'
import { useRouter } from '@/utils/navigation'
import { createBasicAuth, getAuthErrorCode, getAuthErrorStatus, throwAuthRequestError } from '@/stores/auth'
import { FormScrollView, useToast } from '@/components/base'
import { ForgotForm, type ForgotValues } from '@/components/form'
import { ArrowBack } from '@/components/icon'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import ScreenHeader from '@/components/ScreenHeader'

type ForgotStep = 'verify' | 'reset'

export default function ForgotPasswordScreen() {
  const [step, setStep] = useState<ForgotStep>('verify')
  const [resetGrant, setResetGrant] = useState<string | null>(null)
  const router = useRouter()
  const { t } = useTranslation()
  const toast = useToast()
  const loading = useLoadingIndicator()

  const requestOTP = async (code: string, number: string) => {
    const resp = await fetch(`${BASE_URL}/mobile/auth/forgot-password-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        countryCode: code,
        number
      })
    })
    if (!resp.ok) {
      await throwAuthRequestError(resp, 'request password reset code failed')
    }
  }

  const handleVerify = async (data: ForgotValues) => {
    Keyboard.dismiss()
    loading.start()
    try {
      const resp = await fetch(`${BASE_URL}/mobile/auth/verify-forgot-password-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          countryCode: data.phoneNumber.code,
          number: data.phoneNumber.number,
          token: data.verifyCode
        })
      })

      if (!resp.ok) {
        await throwAuthRequestError(resp, 'verify password reset code failed')
      }

      const body: { resetGrant: string } = await resp.json()
      setResetGrant(body.resetGrant)
      setStep('reset')
    } catch (e) {
      const errorCode = getAuthErrorCode(e)
      const status = getAuthErrorStatus(e)
      if (errorCode === 1024 || status === 429) {
        toast.show({ variant: 'error', description: t('auth:toast.error.otpVerificationRateLimited') })
      } else if (errorCode === 1007) {
        toast.show({ variant: 'error', description: t('auth:toast.error.noAccess') })
      } else {
        toast.show({ variant: 'error', description: t('auth:toast.error.otpFailed') })
      }
    } finally {
      loading.stop()
    }
  }

  const handleSubmit = async (data: ForgotValues) => {
    Keyboard.dismiss()
    if (!resetGrant) {
      toast.show({ variant: 'error', description: t('auth:toast.error.otpFailed') })
      setStep('verify')
      return
    }

    if (!/^(?=.*[a-zA-Z])(?=.*\d).{8,}$/.test(data.newPassword)) {
      toast.show({ variant: 'error', description: t('auth:hint.password') })
      return
    }

    if (data.newPassword !== data.newPasswordConfirm) {
      toast.show({ variant: 'error', description: t('auth:hint.mismatch') })
      return
    }

    loading.start()
    try {
      const resp = await fetch(`${BASE_URL}/mobile/auth/finish-forgot-password`, {
        method: 'POST',
        headers: {
          Authorization: createBasicAuth(`${data.phoneNumber.code}${data.phoneNumber.number}`, data.newPassword),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          countryCode: data.phoneNumber.code,
          number: data.phoneNumber.number,
          resetGrant
        })
      })

      if (!resp.ok) {
        await throwAuthRequestError(resp, 'change login password failed')
      }

      toast.show({ variant: 'success', description: t('auth:toast.success.changeLoginPassword') })
      router.replace('/login')
    } catch (e) {
      console.error(e)
      if (getAuthErrorCode(e) === 1007) {
        toast.show({ variant: 'error', description: t('auth:toast.error.noAccess') })
      } else {
        toast.show({ variant: 'error', description: t('auth:toast.error.changeLoginPasswordFailed') })
      }
    } finally {
      loading.stop()
    }
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <Header step={step} />
        }}
      />

      <SafeAreaView className={'flex-1 bg-background-primary'}>
        <FormScrollView className={'pb-4'}>
          <ForgotForm step={step} onVerify={handleVerify} onSubmit={handleSubmit} requestOTP={requestOTP} />
        </FormScrollView>
      </SafeAreaView>
    </>
  )
}

function Header({ step }: { step: ForgotStep }) {
  const { t } = useTranslation()
  const router = useRouter()
  const { top } = useSafeAreaInsets()
  const title = useMemo(() => {
    switch (step) {
      case 'reset':
        return t('auth:changeLoginPassword')
      default:
        return ''
    }
  }, [t, step])

  return (
    <View className={'bg-background-primary'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={
          <Pressable className={'p-2'} onPress={() => router.back()}>
            {({ pressed }) => <ArrowBack className={cn('size-6 text-text-tertiary', pressed && 'opacity-50')} />}
          </Pressable>
        }
        centerElement={<Text className={'text-base font-bold text-text-primary'}>{title}</Text>}
      />
    </View>
  )
}
